import { alpha } from '@mui/material/styles';
import { commonTransitions } from './animations';

// 创建组件样式覆盖
export const createComponentOverrides = (theme) => {
  const { palette, shadows, customShadows } = theme;
  
  return {
    // Card组件增强样式
    MuiCard: {
      styleOverrides: {
        root: {
          position: 'relative',
          borderRadius: 16,
          border: `1px solid ${palette.divider}`,
          boxShadow: customShadows.card,
          transition: commonTransitions.card,
          overflow: 'hidden',
          '&:hover': {
            boxShadow: customShadows.cardHover,
            transform: 'translateY(-4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${palette.primary.main}, ${palette.secondary.main})`,
            opacity: 0,
            transition: commonTransitions.opacity,
          },
          '&:hover::before': {
            opacity: 1,
          },
        },
      },
    },
    
    // Button组件增强样式
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          boxShadow: 'none',
          transition: commonTransitions.button,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 0,
            height: 0,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.3)',
            transition: 'width 0.6s, height 0.6s, top 0.6s, left 0.6s',
            transform: 'translate(-50%, -50%)',
          },
          '&:active::before': {
            width: '300px',
            height: '300px',
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: customShadows.buttonHover,
          },
        },
        contained: {
          '&:hover': {
            boxShadow: customShadows.z8,
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: alpha(palette.primary.main, 0.04),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.04),
          },
        },
      },
    },
    
    // Chip组件样式
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          transition: commonTransitions.all,
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
        filled: {
          '&:hover': {
            boxShadow: customShadows.z4,
          },
        },
      },
    },
    
    // TextField组件增强样式
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: commonTransitions.input,
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.primary.light,
              },
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 2px ${alpha(palette.primary.main, 0.2)}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.primary.main,
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
    
    // Paper组件样式
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${palette.divider}`,
          transition: commonTransitions.shadow,
        },
        elevation1: {
          boxShadow: customShadows.z1,
        },
        elevation4: {
          boxShadow: customShadows.z8,
        },
        elevation8: {
          boxShadow: customShadows.z12,
        },
      },
    },
    
    // Dialog组件样式
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: customShadows.modal,
          border: `1px solid ${palette.divider}`,
        },
      },
    },
    
    // AppBar组件样式
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${palette.divider}`,
          backgroundColor: alpha(palette.background.paper, 0.8),
          backdropFilter: 'blur(8px)',
          transition: commonTransitions.background,
        },
      },
    },
    
    // Drawer组件样式
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${palette.divider}`,
          boxShadow: customShadows.z8,
          backgroundColor: palette.background.paper,
        },
      },
    },
    
    // List组件样式
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 12px',
          transition: commonTransitions.all,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '3px',
            backgroundColor: palette.primary.main,
            transform: 'scaleY(0)',
            transition: 'transform 0.2s ease',
          },
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.08),
            transform: 'translateX(8px)',
            '&::before': {
              transform: 'scaleY(1)',
            },
          },
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, 0.12),
            color: palette.primary.main,
            fontWeight: 600,
            '&::before': {
              transform: 'scaleY(1)',
            },
            '&:hover': {
              backgroundColor: alpha(palette.primary.main, 0.16),
            },
          },
        },
      },
    },
    
    // Alert组件样式
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: 'none',
          boxShadow: customShadows.z1,
          '& .MuiAlert-icon': {
            fontSize: '1.5rem',
          },
        },
        standardSuccess: {
          backgroundColor: alpha(palette.success.main, 0.1),
          color: palette.success.dark,
          border: `1px solid ${alpha(palette.success.main, 0.3)}`,
        },
        standardError: {
          backgroundColor: alpha(palette.error.main, 0.1),
          color: palette.error.dark,
          border: `1px solid ${alpha(palette.error.main, 0.3)}`,
        },
        standardWarning: {
          backgroundColor: alpha(palette.warning.main, 0.1),
          color: palette.warning.dark,
          border: `1px solid ${alpha(palette.warning.main, 0.3)}`,
        },
        standardInfo: {
          backgroundColor: alpha(palette.info.main, 0.1),
          color: palette.info.dark,
          border: `1px solid ${alpha(palette.info.main, 0.3)}`,
        },
      },
    },
    
    // Table组件样式
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${palette.divider}`,
          boxShadow: customShadows.z1,
          overflow: 'hidden',
        },
      },
    },
    
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.neutral,
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: palette.text.primary,
            borderBottom: `2px solid ${palette.divider}`,
          },
        },
      },
    },
    
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: commonTransitions.background,
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.04),
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },
    
    // Loading组件样式
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: alpha(palette.primary.main, 0.1),
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          animationDuration: '1.4s',
        },
      },
    },
    
    // Menu组件样式
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: customShadows.dropdown,
          border: `1px solid ${palette.divider}`,
          marginTop: '8px',
        },
      },
    },
    
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          transition: commonTransitions.all,
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, 0.08),
            transform: 'translateX(4px)',
          },
        },
      },
    },
    
    // Tooltip组件样式
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: customShadows.z8,
          border: `1px solid ${palette.divider}`,
        },
      },
    },
    
    // Avatar组件样式
    MuiAvatar: {
      styleOverrides: {
        root: {
          transition: commonTransitions.transform,
          '&:hover': {
            transform: 'scale(1.1)',
          },
        },
      },
    },
    
    // Badge组件样式
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
          fontSize: '0.75rem',
          minWidth: '20px',
          height: '20px',
          borderRadius: '10px',
        },
      },
    },
    
    // Switch组件样式
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 58,
          height: 38,
          padding: 7,
        },
        switchBase: {
          margin: 1,
          padding: 0,
          transform: 'translateX(6px)',
          '&.Mui-checked': {
            color: '#fff',
            transform: 'translateX(22px)',
            '& .MuiSwitch-thumb:before': {
              backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent('#fff')}" d="m8.229 14.062-3.521-3.541L5.75 9.479l2.479 2.459 6.021-6L15.292 7Z"/></svg>')`,
            },
            '& + .MuiSwitch-track': {
              opacity: 1,
              backgroundColor: palette.primary.main,
            },
          },
        },
        thumb: {
          backgroundColor: '#fff',
          width: 32,
          height: 32,
          '&:before': {
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent('#999')}" d="M15.95 10.78 13.78 8.61a.5.5 0 0 0-.86.35l.5 2.83H8.5a.5.5 0 0 0 0 1h4.92l-.5 2.83a.5.5 0 0 0 .86.35l2.17-2.17a.5.5 0 0 0 0-.71Z"/></svg>')`,
          },
        },
        track: {
          opacity: 1,
          backgroundColor: palette.grey[400],
          borderRadius: 20 / 2,
        },
      },
    },
    
    // Slider组件样式
    MuiSlider: {
      styleOverrides: {
        root: {
          color: palette.primary.main,
          height: 8,
        },
        track: {
          border: 'none',
          height: 8,
          borderRadius: 4,
        },
        rail: {
          color: palette.grey[300],
          opacity: 1,
          height: 8,
          borderRadius: 4,
        },
        thumb: {
          height: 24,
          width: 24,
          backgroundColor: '#fff',
          border: `2px solid ${palette.primary.main}`,
          '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: `0px 0px 0px 8px ${alpha(palette.primary.main, 0.16)}`,
          },
          '&:before': {
            display: 'none',
          },
        },
        valueLabel: {
          lineHeight: 1.2,
          fontSize: 12,
          background: 'unset',
          padding: 0,
          width: 32,
          height: 32,
          borderRadius: '50% 50% 50% 0',
          backgroundColor: palette.primary.main,
          transformOrigin: 'bottom left',
          transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
          '&:before': { display: 'none' },
          '&.MuiSlider-valueLabelOpen': {
            transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
          },
          '& > *': {
            transform: 'rotate(45deg)',
          },
        },
      },
    },
    
    // Stepper组件样式
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: '24px 0',
        },
      },
    },
    
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.875rem',
          fontWeight: 500,
          '&.Mui-active': {
            fontWeight: 600,
            color: palette.primary.main,
          },
          '&.Mui-completed': {
            fontWeight: 500,
            color: palette.text.secondary,
          },
        },
      },
    },
    
    MuiStepIcon: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem',
          '&.Mui-active': {
            color: palette.primary.main,
          },
          '&.Mui-completed': {
            color: palette.success.main,
          },
        },
      },
    },
    
    // Tabs组件样式
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
          borderBottom: `1px solid ${palette.divider}`,
        },
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: palette.primary.main,
        },
      },
    },
    
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 48,
          padding: '12px 16px',
          color: palette.text.secondary,
          transition: commonTransitions.all,
          '&:hover': {
            color: palette.primary.main,
            backgroundColor: alpha(palette.primary.main, 0.04),
          },
          '&.Mui-selected': {
            color: palette.primary.main,
            fontWeight: 600,
          },
        },
      },
    },
    
    // Accordion组件样式
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${palette.divider}`,
          boxShadow: 'none',
          '&:not(:last-child)': {
            borderBottom: 0,
          },
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 'auto',
          },
        },
      },
    },
    
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.neutral,
          borderBottom: `1px solid ${palette.divider}`,
          marginBottom: -1,
          minHeight: 56,
          '&.Mui-expanded': {
            minHeight: 56,
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
        },
      },
    },
  };
};

export default createComponentOverrides;